import { useState, useEffect } from 'react';
import { Users, TrendingDown, Clock, Sparkles } from 'lucide-react';
import MetricCard from '@/components/MetricCard';
import ChartWidget from '@/components/ChartWidget';
import DataTable from '@/components/DataTable';
import LoadingSpinner from '@/components/LoadingSpinner';
import type { MetricData } from '@/types';
import api from '@/api/client';

const MOCK_METRICS: MetricData[] = [
  { name: 'Total Headcount', value: 1247, change: 4.2, changeType: 'increase' },
  { name: 'Attrition Rate', value: 12.3, change: 1.8, changeType: 'decrease' },
  { name: 'Avg Tenure (yrs)', value: 3.4, change: 0.3, changeType: 'increase' },
  { name: 'Diversity Index', value: 0.72, change: 5.1, changeType: 'increase' },
];

const METRIC_ICONS = [
  <Users className="h-5 w-5" />,
  <TrendingDown className="h-5 w-5" />,
  <Clock className="h-5 w-5" />,
  <Sparkles className="h-5 w-5" />,
];

const MOCK_HEADCOUNT_DATA = [
  { month: 'Jul', headcount: 1102 },
  { month: 'Aug', headcount: 1125 },
  { month: 'Sep', headcount: 1148 },
  { month: 'Oct', headcount: 1163 },
  { month: 'Nov', headcount: 1189 },
  { month: 'Dec', headcount: 1201 },
  { month: 'Jan', headcount: 1218 },
  { month: 'Feb', headcount: 1230 },
  { month: 'Mar', headcount: 1247 },
];

const MOCK_ATTRITION_BY_DEPT = [
  { name: 'Engineering', rate: 9.2 },
  { name: 'Sales', rate: 18.4 },
  { name: 'Marketing', rate: 14.1 },
  { name: 'Product', rate: 7.8 },
  { name: 'Operations', rate: 11.5 },
  { name: 'HR', rate: 6.3 },
  { name: 'Finance', rate: 8.9 },
];

const MOCK_DIVERSITY_DATA = [
  { name: 'Women', value: 44 },
  { name: 'Men', value: 52 },
  { name: 'Non-binary', value: 4 },
];

const MOCK_RECENT_HIRES = [
  { name: 'Sarah Chen', department: 'Engineering', title: 'Senior SWE', startDate: '2026-03-15', location: 'San Francisco' },
  { name: 'Marcus Johnson', department: 'Sales', title: 'Account Executive', startDate: '2026-03-10', location: 'New York' },
  { name: 'Priya Patel', department: 'Product', title: 'Product Manager', startDate: '2026-03-08', location: 'Remote' },
  { name: 'Alex Kim', department: 'Marketing', title: 'Content Strategist', startDate: '2026-03-03', location: 'Austin' },
  { name: 'Jordan Lee', department: 'Engineering', title: 'Staff Engineer', startDate: '2026-03-01', location: 'Seattle' },
];

const recentHireColumns = [
  { key: 'name', header: 'Name' },
  { key: 'department', header: 'Department' },
  { key: 'title', header: 'Title' },
  { key: 'startDate', header: 'Start Date' },
  { key: 'location', header: 'Location' },
];

export default function DashboardPage() {
  const [metrics, setMetrics] = useState<MetricData[]>(MOCK_METRICS);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    async function fetchMetrics() {
      try {
        const data = await api.get<{ metrics: MetricData[] }>(
          '/api/v1/metrics/query'
        );
        if (data.metrics && data.metrics.length > 0) {
          setMetrics(data.metrics);
        }
      } catch {
        // Use mock data as fallback - already set
      } finally {
        setIsLoading(false);
      }
    }
    fetchMetrics();
  }, []);

  if (isLoading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">
          Executive Dashboard
        </h1>
        <p className="mt-1 text-sm text-gray-500">
          Key workforce metrics and trends at a glance
        </p>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
        {metrics.map((metric, idx) => (
          <MetricCard key={metric.name} metric={metric} icon={METRIC_ICONS[idx]} />
        ))}
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        <ChartWidget
          title="Headcount Over Time"
          type="line"
          data={MOCK_HEADCOUNT_DATA}
          dataKey="headcount"
          xKey="month"
        />
        <ChartWidget
          title="Attrition Rate by Department (%)"
          type="bar"
          data={MOCK_ATTRITION_BY_DEPT}
          dataKey="rate"
          xKey="name"
          colors={['#DC2626']}
        />
      </div>

      {/* Bottom Row */}
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        <ChartWidget
          title="Gender Diversity Breakdown"
          type="pie"
          data={MOCK_DIVERSITY_DATA}
          dataKey="value"
          xKey="name"
          height={280}
        />
        <div className="card">
          <h3 className="mb-4 text-base font-semibold text-gray-900">
            Recent Hires
          </h3>
          <DataTable
            columns={recentHireColumns}
            data={MOCK_RECENT_HIRES}
            pageSize={5}
          />
        </div>
      </div>
    </div>
  );
}
